// Shared code for the /typefaces page and typefaces overview widgets.

import { Timer } from "./Timer";

class SlidesCycler {
  constructor(slidesElement) {
    this.slides = Array.from(
      slidesElement.querySelectorAll(".tile__slide--cyclable"),
    );
    this.startSlide = slidesElement.querySelector(".tile__slide--start");
    this.currentIndex = null;
    this.previousSlide = null;
  }

  next() {
    const outgoing =
      this.currentIndex === null
        ? this.startSlide
        : this.slides[this.currentIndex];
    this.currentIndex = ((this.currentIndex ?? -1) + 1) % this.slides.length;
    const incoming = this.slides[this.currentIndex];

    this.previousSlide?.classList.remove("tile__slide--out");
    if (outgoing) {
      this.previousSlide = outgoing;
      outgoing.classList.remove("tile__slide--current");
      outgoing.classList.add("tile__slide--out");
    }
    incoming?.classList.add("tile__slide--current");
  }
}

class SlidesCyclerSet {
  constructor(root, colors) {
    this.root = root;
    this.colors = colors;
    this.colorIndex = 0;
    this.cyclers = Array.from(root.querySelectorAll(".tile__slides")).map(
      (element) => new SlidesCycler(element),
    );
    this.timer = new Timer(() => this.next(), {
      interval: 3200,
      initialDelay: 3000,
    });
  }

  next() {
    this.cyclers.forEach((cycler) => cycler.next());
    updateColors(this.root, this.colors, ++this.colorIndex);
  }

  pause() {
    this.timer.pause();
  }

  resume() {
    this.timer.resume();
  }

  destroy() {
    this.timer.destroy();
  }
}

export class TypefacesOverview {
  constructor(root, { animated, colors }) {
    this.root = root;
    this.colors = colors;
    this.animated = animated;
    this.init();
    this.cycler = animated ? new SlidesCyclerSet(root, colors) : null;
  }

  init() {
    updateColors(this.root, this.colors);

    this.root.querySelectorAll(".rendered_text").forEach((element) => {
      const fontScaler = new window.FontScaler(element, {
        scaleUp: false,
        downSpeed: 65,
      });
      fontScaler.start();
    });

    highlightSeparatorWhenAdjacentTileIsHovered(this.root);
  }

  destroy() {
    this.cycler?.destroy();
  }
}

// ----- Functions -----

function updateColors(root, fontFamilyColors, colorIndex = 0) {
  const regularColor = colorAt(fontFamilyColors.regular, colorIndex);
  const betaColor = colorAt(fontFamilyColors.beta, colorIndex);

  root
    .querySelectorAll(".released-fonts .tile__slide--current .rendered_text")
    .forEach((renderedTextElement) => {
      renderedTextElement.style.color = regularColor;
    });

  root.querySelectorAll(".beta-fonts").forEach((container) => {
    container.style.backgroundColor = betaColor;

    const dynamicBackgroundEl = container.querySelector(".dynamic-background");
    if (dynamicBackgroundEl) {
      dynamicBackgroundEl.style.backgroundColor = betaColor;
    }
  });
}

function colorAt(palette, index) {
  if (!palette || palette.length === 0) return "black";
  return palette[index % palette.length];
}

// ----------------------------------------
// Highlighting separator lines
//
// We have thin separator lines between tiles that belong to the same group. (e.g. multiple packages in the same font-family)
// These lines are within one of the adjacent tiles, when that tile is hovered, its background turns black, and since the line is also black with a low opacity, it will also be black.
// The problem is that if the other adjacent tile is hovered, a thin line will still be visible next to it.
// To solve this, we add a class to the separator line when the adjacent tile is hovered, and remove it when the mouse leaves the tile.
//
function highlightSeparatorWhenAdjacentTileIsHovered(root) {
  root.querySelectorAll(".tile").forEach((tile) => {
    tile.addEventListener("mouseenter", () => {
      addHoverClassToSiblingOnTheLeft(tile);
      addHoverClassToSiblingAbove(tile);
    });
  });
}

function addHoverClassToSiblingOnTheLeft(currentTile) {
  const adjacentTile = currentTile.previousElementSibling;
  if (!adjacentTile) return;

  const separator = adjacentTile.querySelector(".tile__column-separator");
  const crosshair = adjacentTile.querySelector(".tile__separator-crosshair");

  if (separator) {
    currentTile.addEventListener(
      "mouseleave",
      () => {
        separator.classList.remove("adjacent-tile-hovered");
        if (crosshair) crosshair.classList.remove("adjacent-tile-hovered");
      },
      { once: true },
    );
    separator.classList.add("adjacent-tile-hovered");
    if (crosshair) crosshair.classList.add("adjacent-tile-hovered");
  }
}

function addHoverClassToSiblingAbove(currentTile) {
  const currentRow = currentTile.closest(".tile-row");
  if (!currentRow) return;

  const previousRow = currentRow.previousElementSibling;
  if (!previousRow) return;

  const tileIndex = Array.from(currentRow.querySelectorAll(".tile")).indexOf(
    currentTile,
  );
  if (tileIndex === -1) return;

  const tileTop = previousRow.querySelectorAll(".tile")[tileIndex];
  if (!tileTop) return;

  const separator = tileTop.querySelector(".tile__row-separator");
  const tileTopLeft = tileTop.previousElementSibling;
  const crosshairTopLeft = tileTopLeft?.querySelector(
    ".tile__separator-crosshair",
  );
  const crosshairTopRight = tileTop.querySelector(".tile__separator-crosshair");

  if (separator) {
    currentTile.addEventListener(
      "mouseleave",
      () => {
        separator.classList.remove("adjacent-tile-hovered");
        if (crosshairTopLeft)
          crosshairTopLeft.classList.remove("adjacent-tile-hovered");
        if (crosshairTopRight)
          crosshairTopRight.classList.remove("adjacent-tile-hovered");
      },
      { once: true },
    );
    separator.classList.add("adjacent-tile-hovered");
    if (crosshairTopLeft)
      crosshairTopLeft.classList.add("adjacent-tile-hovered");
    if (crosshairTopRight)
      crosshairTopRight.classList.add("adjacent-tile-hovered");
  }
}
